/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { canadianPayrollAPI } from '../../lib/api';
import { useCanadianPayroll } from '../../context/CanadianPayrollContext';
import { toast } from 'sonner';
import { FileDown, Loader2 } from 'lucide-react';

/**
 * CanadianPayslipPdfButton — additive download button for CA-eligible payslips.
 *
 * • Hidden entirely when tenant is not CA-eligible.
 * • Hidden silently if the specific payslip is non-CA (403 → silent skip).
 * • Does NOT replace the existing payslip PDF — appears alongside it.
 */
export function CanadianPayslipPdfButton({ payslipId, employeeName, periodStart, className = '' }) {
  const { caEnabled } = useCanadianPayroll();
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  if (!caEnabled || unavailable) return null;

  const handle = async () => {
    if (!payslipId) return;
    setBusy(true);
    try {
      const r = await canadianPayrollAPI.downloadPayslipPdf(payslipId);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (employeeName || 'employee').replace(/\s+/g, '_');
      a.download = `payslip-CA-${safeName}-${periodStart || 'period'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 403) {
        setUnavailable(true);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to download Canadian payslip');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={handle}
      disabled={busy}
      variant="outline"
      className={`border-red-200 text-red-700 hover:bg-red-50 ${className}`}
      data-testid="ca-payslip-pdf-btn"
      title="Download Canadian payslip PDF (with YTD + province info)"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
      Canadian Payslip PDF
    </Button>
  );
}

export default CanadianPayslipPdfButton;
